<template>
  <BasePhoneRow
    :id="'item-' + opportunity.id"
    class="opportunity-list-item"
    :href="href"
    :selectable="selectable"
    :selected="selected"
    @open="$emit('open')"
    @toggle-select="$emit('toggle-select')"
  >
    <template #lead>
      <OpportunityStatusType v-if="opportunity.status" :opportunity-status="opportunity.status" condensed />
    </template>
    <template #title>
      <TextHighlight class="base-phone-row__title" :content="opportunity.name" isPlain />
    </template>

    <p v-if="contextLine" class="base-phone-row__line opportunity-list-item__context">{{ contextLine }}</p>

    <div class="base-phone-row__meta">
      <span
        v-if="opportunity.status"
        class="opportunity-list-item__status"
        :class="{ 'is-updating': statusUpdating }"
        :aria-busy="String(statusUpdating)"
      >
        <BaseStatusBadge :text="opportunity.status.name" :color="opportunity.status.color" is-small />
        <BaseCircularLoadingBar v-if="statusUpdating" :size="12" :width="2" color="accent" />
      </span>

      <BaseTag v-if="countdown" icon="clock" preset="soft" :tone="countdownTone">{{ countdown }}</BaseTag>
      <TenderStatusTag
        v-else-if="opportunity.tender && opportunity.tender.status"
        :status="opportunity.tender.status"
      />

      <template v-if="showVotes">
        <BaseVoteTicket v-if="finalVote" :name="$t(`go-nogo.${finalVote.toLowerCase()}`)" :vote="finalVote" is-final />
        <span v-else-if="votes.length" class="opportunity-list-item__indicator" role="img" :aria-label="votesLabel">
          <BaseIcon name="vote" aria-hidden="true" />{{ castVotes }}/{{ votes.length }}
        </span>
      </template>

      <template v-if="labels.length">
        <BaseTag :background="labels[0].color">{{ labels[0].name }}</BaseTag>
        <span v-if="labels.length > 1" class="opportunity-list-item__indicator">+{{ labels.length - 1 }}</span>
      </template>

      <span
        v-if="assignees.length"
        class="opportunity-list-item__indicator"
        role="img"
        :aria-label="`${$t('page.opportunities.data-table-titles.assignees')} : ${assignees.length}`"
      >
        <BaseIcon name="users" aria-hidden="true" />{{ assignees.length }}
      </span>
      <span
        v-if="showDocuments && documents.length"
        class="opportunity-list-item__indicator"
        role="img"
        :aria-label="`${$t('page.opportunities.data-table-titles.documents')} : ${documents.length}`"
      >
        <BaseIcon name="file" aria-hidden="true" />{{ documents.length }}
      </span>
      <span
        v-for="oppSync in startedSyncs"
        :key="oppSync.profile.id"
        class="opportunity-list-item__sync"
        role="img"
        :aria-label="syncLabel(oppSync)"
      >
        <BaseIcon :name="syncIcon(oppSync)" aria-hidden="true" />
      </span>
    </div>

    <template v-if="!selectable" #actions>
      <button
        type="button"
        class="base-phone-row__action"
        :aria-label="$t('actions.more-actions')"
        aria-haspopup="dialog"
        @click.stop="$emit('more')"
      >
        <BaseIcon name="more-horizontal" aria-hidden="true" />
      </button>
    </template>
  </BasePhoneRow>
</template>

<script>
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BasePhoneRow from '@/components/base/BasePhoneRow.vue';
import BaseStatusBadge from '@/components/base/BaseStatusBadge.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseVoteTicket from '@/components/base/BaseVoteTicket';
import OpportunityStatusType from '@/components/page-admin-opportunities/OpportunityStatusType.vue';
import TenderStatusTag from '@/components/page-tender-details/TenderStatusTag.vue';
import TextHighlight from '@/components/text-highlight/TextHighlight';
import PlatformName from '@/mixins/platform-name-mixin';
import { getDhm } from '@/util/date-util';
import { getEntitySyncIcon, getEntitySyncLabel } from '@/util/entity-sync-util';
import { toFinalDecisionDisplayVote } from '@/util/go-nogo-final-decision';
import { paths } from '@/util/paths';
import { getClosingCountdownTone } from '@/util/tender-closing-countdown';

// One opportunity in the phone list: where it stands and what's next. The rest is on its page.
export default {
  name: 'OpportunityListItem',
  mixins: [PlatformName],
  components: {
    BaseCircularLoadingBar,
    BaseIcon,
    BasePhoneRow,
    BaseStatusBadge,
    BaseTag,
    BaseVoteTicket,
    OpportunityStatusType,
    TenderStatusTag,
    TextHighlight,
  },
  props: {
    opportunity: {
      type: Object,
      required: true,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    statusUpdating: {
      type: Boolean,
      default: false,
    },
    showVotes: {
      type: Boolean,
      default: false,
    },
    showDocuments: {
      type: Boolean,
      default: false,
    },
    // `{ profile, sync }` for each active sync profile, as the desktop table has them.
    syncs: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    href() {
      return paths.opportunityDetails.replace(':opportunityId', this.opportunity.id);
    },
    // As in the desktop table: the tender's reference, or where the opportunity came from.
    contextLine() {
      const { emitter, tender, source } = this.opportunity;
      const origin = tender ? tender.source.referenceNumber : this.getOpportunitySourceName(source);
      return [emitter, origin].filter(Boolean).join(' · ');
    },
    // The tender's closing when it has one, as in the desktop table.
    daysBeforeClosing() {
      const { tender } = this.opportunity;
      if (tender && tender.tenderPeriod && tender.tenderPeriod.endDate) return tender.daysBeforeClosing;
      return this.opportunity.daysBeforeClosing;
    },
    countdown() {
      return this.daysBeforeClosing ? getDhm(this.daysBeforeClosing).split(' ')[0] : '';
    },
    countdownTone() {
      return getClosingCountdownTone(this.daysBeforeClosing);
    },
    finalVote() {
      const { finalDecision } = this.opportunity;
      return finalDecision ? toFinalDecisionDisplayVote(finalDecision.vote) : null;
    },
    votes() {
      return this.opportunity.votes || [];
    },
    castVotes() {
      return this.votes.filter((vote) => !vote.isPending).length;
    },
    votesLabel() {
      return this.$t('go-nogo.votes-cast', { cast: this.castVotes, invited: this.votes.length });
    },
    labels() {
      return this.opportunity.labels || [];
    },
    assignees() {
      return this.opportunity.assignees || [];
    },
    documents() {
      return this.opportunity.documents || [];
    },
    // Only the syncs that ran: one never started is an action, in the row's sheet.
    startedSyncs() {
      return this.syncs.filter((oppSync) => oppSync.sync && oppSync.sync.status);
    },
  },
  methods: {
    syncIcon(oppSync) {
      return getEntitySyncIcon(oppSync.profile, oppSync.sync);
    },
    syncLabel(oppSync) {
      return getEntitySyncLabel(oppSync.profile, oppSync.sync);
    },
  },
};
</script>

<style lang="scss">
// The row is BasePhoneRow's; here, what only an opportunity shows.
.opportunity-list-item {
  .base-phone-row__lead {
    min-width: 18px;
    margin-top: 1px;
  }

  .base-phone-row__check {
    margin-top: 0;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &.is-updating .opportunity-status-badge {
      opacity: 0.5;
    }
  }

  &__indicator {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 12px;
    color: $color-slate-500;

    .base-icon {
      width: 14px;
      height: 14px;
    }
  }

  &__sync {
    display: inline-flex;

    .base-icon {
      width: 18px;
      height: 18px;
    }
  }
}
</style>
